import React, { useRef, useState } from "react";

import { faCheck, faWifiSlash } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faSpinnerScale } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { differenceInSeconds } from "date-fns";

import { type NoteSaveStatus, useNoteSaveState } from "~/meetings/NoteSaveStateContext";
import Tooltip from "~/ui/Tooltip";

import styles from "./SaveState.module.css";

export default function SaveState() {
	const { saveStatus } = useNoteSaveState();
	const prevSaveStatus = useRef<NoteSaveStatus>(saveStatus);

	const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

	if (prevSaveStatus.current !== saveStatus) {
		// If we're moving away from a saved state, store the last moment that things were in a saved state
		if (prevSaveStatus.current === "saved") {
			setLastSavedAt(new Date());
		}

		prevSaveStatus.current = saveStatus;
	}

	switch (saveStatus) {
		case "unknown": {
			return null;
		}
		case "offline": {
			return (
				<div className={styles.delayedIndicatorWrapper}>
					<Tooltip visible content="We're having connectivity issues" appendTo="parent">
						<FontAwesomeIcon icon={faWifiSlash} className={cx(styles.indicator, styles.offline)} fixedWidth />
					</Tooltip>
				</div>
			);
		}
		case "saving": {
			return (
				<div className={styles.delayedIndicatorWrapper}>
					<FontAwesomeIcon icon={faSpinnerScale} className={cx(styles.indicator, styles.saving)} fixedWidth spinPulse />
				</div>
			);
		}
		case "saved": {
			return (
				<FontAwesomeIcon
					icon={faCheck}
					className={cx(styles.indicator, styles.saved, {
						// Only show the checkmark if it's been more than 3 seconds since the last time things were saved
						[styles.showAndFadeOut]: lastSavedAt && differenceInSeconds(new Date(), lastSavedAt) > 3,
					})}
					fixedWidth
					data-test-id="note-saved-check"
				/>
			);
		}
	}
}
